/**
 * Bedrock IAM Setup Instructions
 *
 * Shared component used in both BedrockConfigDialog and BedrockConfigSection.
 * Walks the user through creating the IAM role and attaching the inline policy.
 */

import { useState } from "react";
import { Copy, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import type { BedrockSetupInstructions as BedrockSetupInstructionsType } from "@/types/bedrock";

const INLINE_POLICY = JSON.stringify(
  {
    Version: "2012-10-17",
    Statement: [
      {
        Effect: "Allow",
        Action: [
          "logs:FilterLogEvents",
          "logs:DescribeLogGroups",
          "logs:DescribeLogStreams",
        ],
        Resource: "*",
      },
    ],
  },
  null,
  2
);

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
      // optionally surface a toast/message to copy manually
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={handleCopy}
      aria-label={`Copy ${label}`}
      className="h-7 px-2"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-green-600" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </Button>
  );
}

interface BedrockSetupInstructionsProps {
  setupInstructions: BedrockSetupInstructionsType | undefined;
  isLoading: boolean;
}

export function BedrockSetupInstructions({
  setupInstructions,
  isLoading,
}: BedrockSetupInstructionsProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Generating your unique external ID…
      </div>
    );
  }

  if (!setupInstructions) return null;

  const trustPolicyJson = JSON.stringify(setupInstructions.trust_policy, null, 2);

  return (
    <div className="space-y-5 pt-3">
      <p className="text-sm text-muted-foreground">
        Follow these steps in your AWS account to create the IAM role Vortal will use to read
        your Bedrock logs.
      </p>

      {/* Step A */}
      <div className="space-y-3">
        <p className="text-sm font-medium">
          1. Go to <strong>AWS Console → IAM → Roles → Create role</strong>
        </p>
        <p className="text-sm font-medium">
          2. Select <strong>Custom trust policy</strong> and paste the JSON below, then click{" "}
          <strong>Next</strong>
        </p>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Trust Policy
            </Label>
            <CopyButton value={trustPolicyJson} label="trust policy" />
          </div>
          <pre className="rounded-md bg-background border px-3 py-2 text-xs font-mono overflow-x-auto max-h-44">
            {trustPolicyJson}
          </pre>
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              External ID (already included above)
            </Label>
            <CopyButton value={setupInstructions.external_id} label="external ID" />
          </div>
          <div className="rounded-md bg-background border px-3 py-2 font-mono text-sm select-all">
            {setupInstructions.external_id}
          </div>
        </div>
      </div>

      {/* Step B */}
      <div className="space-y-3">
        <p className="text-sm font-medium">
          3. On the <strong>Add permissions</strong> step — <span className="text-muted-foreground">skip it</span>, just click <strong>Next</strong>. The
          permissions needed are not in Amazon's managed policy list; you'll add them as a custom
          inline policy in the next step.
        </p>
        <p className="text-sm font-medium">
          4. Give the role a name (e.g. <code className="text-xs bg-muted px-1 py-0.5 rounded">VortalBedrockRole</code>) and click{" "}
          <strong>Create role</strong>
        </p>
      </div>

      {/* Step C — inline policy */}
      <div className="space-y-3">
        <p className="text-sm font-medium">
          5. Open the role you just created → <strong>Permissions</strong> tab →{" "}
          <strong>Add permissions → Create inline policy</strong>
        </p>
        <p className="text-sm font-medium">
          6. Switch to the <strong>JSON</strong> tab, paste the policy below, click{" "}
          <strong>Next</strong>, name it (e.g.{" "}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">VortalCloudWatchLogsRead</code>
          ), then <strong>Create policy</strong>
        </p>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Inline Policy (CloudWatch Logs read access)
            </Label>
            <CopyButton value={INLINE_POLICY} label="inline policy" />
          </div>
          <pre className="rounded-md bg-background border px-3 py-2 text-xs font-mono overflow-x-auto max-h-44">
            {INLINE_POLICY}
          </pre>
        </div>
      </div>

      {/* Step D */}
      <div className="space-y-1">
        <p className="text-sm font-medium">
          7. Copy the role's <strong>ARN</strong> from the role summary page and paste it into the
          form below.
        </p>
        <p className="text-xs text-muted-foreground">
          The ARN looks like:{" "}
          <code className="bg-muted px-1 py-0.5 rounded">
            arn:aws:iam::123456789012:role/VortalBedrockRole
          </code>
        </p>
      </div>
    </div>
  );
}
